import { useMemo } from 'react';
import useAsyncMemo from 'hooks/useAsyncMemo';
import useMarketingParams from 'hooks/useMarketingParams';
import { useRouter } from 'next/router';
import useConfig from 'services/config';
import { notEmpty } from 'utils/arrays';

import { queryTenantApi } from './client';
import { buildAvailableYearOptions, resolveProductOptionsForUbaYears } from './helpers';
import { GetProductOptionsRequest, GetProductOptionsResponse } from './types';

export function useProductOptionsData(
  tenant: string,
  { utmParams, couponCodes, skip }: GetProductOptionsRequest & { skip?: boolean } = {},
) {
  return useAsyncMemo<GetProductOptionsResponse>(async () => {
    if (skip) return;

    const response = await queryTenantApi(tenant, {
      url: '/configuration/product-options',
      tags: ['ProductOptions'],
      query: {
        ...(utmParams ? utmParams : {}),
        ...(couponCodes?.length ? { couponCodes: couponCodes.join(',') } : {}),
      },
    });

    return response;
  }, [utmParams, couponCodes, skip]);
}

export interface ProductOptionsConfig {
  tenant?: string;
  defaultVehicleClass?: string;
  defaultProductOption?: string;
}

export function useProductOptions(
  selectedVehicleClass?: string,
  selectedUbaYears?: string,
  config: ProductOptionsConfig = {},
) {
  const { tenant: defaultTenant } = useConfig();
  const tenant = config.tenant || defaultTenant;
  const params = useMarketingParams();
  const { isReady } = useRouter();
  const data = useProductOptionsData(tenant, { ...params, skip: !isReady });

  return useMemo(() => {
    const priceMatrix = data?.activeConfigurations;
    const selectedConfiguration =
      priceMatrix && ((selectedUbaYears && priceMatrix[parseInt(selectedUbaYears)]) || Object.values(priceMatrix)[0]);

    if (!priceMatrix || !selectedConfiguration)
      return {
        isLoading: !data,
        defaultVehicleClass: config.defaultVehicleClass,
      };

    const { defaultVehicleClass = config.defaultVehicleClass || 'M1' } = selectedConfiguration;
    const { availableYears, hasComboOptions } = buildAvailableYearOptions(
      priceMatrix,
      selectedVehicleClass || defaultVehicleClass,
    );

    const productOptions = resolveProductOptionsForUbaYears(
      priceMatrix,
      (selectedUbaYears || availableYears[0].key).split(','),
      selectedVehicleClass || defaultVehicleClass,
    );

    const availableVehicleClasses =
      productOptions[0] &&
      Object.entries('prices' in productOptions[0] ? productOptions[0].prices : productOptions[0].relativeAmounts)
        .map(([key, value]) => (value ? key : undefined))
        .filter(notEmpty);

    return {
      isLoading: false,
      availableYears,
      hasComboOptions,
      productOptions,
      availableVehicleClasses,
      defaultVehicleClass,
    };
  }, [data, selectedVehicleClass, selectedUbaYears, config.defaultVehicleClass]);
}
